/* Measures only cart shipping text in the drawer and full cart page. Native cart, pricing and checkout remain untouched. */
(() => {
  const installationKey = '__afzCartCompact20261012';
  if (window[installationKey]) return;
  window[installationKey] = true;

  const records = new Map();
  let roots = [];
  const rootObservers = new Map();
  let frame = 0;

  const measure = () => {
    frame = 0;
    for (const [bar, record] of records) {
      if (!bar.isConnected) continue;
      const available = record.viewport.clientWidth;
      if (!available) continue;
      const distance = Math.max(0, record.track.scrollWidth - available);
      const overflow = distance > 1;
      record.overflow = String(overflow);
      bar.setAttribute('data-afz-shipping-overflow', record.overflow);
      record.track.style.setProperty('--afz-shipping-distance', `${Math.ceil(distance)}px`);
      record.track.style.setProperty('--afz-shipping-duration', `${Math.max(8, distance / 20 + 3).toFixed(2)}s`);
    }
  };

  const queueMeasure = () => {
    if (!frame) frame = window.requestAnimationFrame(measure);
  };

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(queueMeasure) : null;

  const forget = (bar, record) => {
    resizeObserver?.unobserve(record.viewport);
    resizeObserver?.unobserve(record.track);
    records.delete(bar);
  };

  const refresh = () => {
    for (const [bar, record] of records) {
      if (!roots.some((root) => root.contains(bar)) || !bar.contains(record.track)) forget(bar, record);
    }
    if (roots.length === 0) return;

    for (const root of roots) for (const bar of root.querySelectorAll('.afz-cart-shipping')) {
      if (records.has(bar)) continue;
      const viewport = bar.querySelector('[data-afz-shipping-viewport], .afz-cart-shipping__viewport')
        || Array.from(bar.children).find((child) => child.tagName === 'SPAN');
      if (!viewport) continue;
      viewport.classList.add('afz-cart-shipping__viewport');

      let track = viewport.querySelector('[data-afz-shipping-track], .afz-cart-shipping__track');
      if (!track) {
        track = document.createElement('span');
        track.className = 'afz-cart-shipping__track';
        track.setAttribute('data-afz-shipping-track', '');
        while (viewport.firstChild) track.append(viewport.firstChild);
        viewport.append(track);
        bar.setAttribute('data-afz-shipping-legacy', '');
      } else {
        track.classList.add('afz-cart-shipping__track');
      }

      const record = { viewport, track };
      records.set(bar, record);
      resizeObserver?.observe(viewport);
      resizeObserver?.observe(track);
    }
    queueMeasure();
  };

  const findRoots = () => ['cart-drawer', 'afz-cart-page'].map((id) => document.getElementById(id)).filter(Boolean);
  const attachRoots = () => {
    const nextRoots = findRoots();
    for (const [root, observer] of rootObservers) {
      if (!nextRoots.includes(root)) { observer.disconnect(); rootObservers.delete(root); }
    }
    roots = nextRoots;
    for (const root of roots) {
      if (rootObservers.has(root)) continue;
      const observer = new MutationObserver((mutations) => {
        const shippingChanged = mutations.some(({ type, target, attributeName, addedNodes, removedNodes }) => {
          if (type === 'attributes') {
            if (attributeName === 'open') return true;
            const record = records.get(target);
            return !!record && target.getAttribute('data-afz-shipping-overflow') !== record.overflow;
          }
          if (target.parentElement?.closest('.afz-cart-shipping') || target.closest?.('.afz-cart-shipping')) return true;
          return [...addedNodes, ...removedNodes].some((node) => node.nodeType === 1
            && (node.matches?.('.afz-cart-shipping') || node.querySelector?.('.afz-cart-shipping')));
        });
        if (shippingChanged) refresh();
      });
      observer.observe(root, {
        childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['open', 'data-afz-shipping-overflow'],
      });
      rootObservers.set(root, observer);
    }
    refresh();
  };

  const start = () => {
    attachRoots();
    // Section rendering can replace the whole drawer; only root identity changes need rebinding.
    const rootObserver = new MutationObserver(() => {
      const nextRoots = findRoots();
      if (nextRoots.length !== roots.length || nextRoots.some((root, index) => root !== roots[index])) attachRoots();
    });
    rootObserver.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', attachRoots);
    window.addEventListener('pageshow', queueMeasure);
    if (!resizeObserver) window.addEventListener('resize', queueMeasure, { passive: true });
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener?.('change', queueMeasure);
    document.fonts?.ready.then(queueMeasure);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

